import {
  AssetPositionRow,
  AssetRow,
  DEFAULT_ASSET_REGISTER_DATA_EXPORT_COLUMN_ORDER,
  LocationRow,
} from "components/Table/AssetRegisterData";
import dayjs from "dayjs";
import type { AssetRegisterAggregatedTimeSeriesResponse } from "kubb";
import type React from "react";

import numberFormatter from "components/Table/Formatters";
import type { BillingData } from "components/Table/model";
import PrivacyModeDataTable, {
  type PrivacyModeTableColumn,
} from "components/Text/PrivacyModeDataTableCell";
import { aggregatedDataCSVExport } from "components/TimeSeries/AggregatedTimeSeriesDataReportView/AggregatedTImeSeriesDataReportView.tsx";
import CSVExportButton from "components/utils/CSVExport/CSVExportButton";
import {
  assetPositionSortValue,
  locationSortValue,
} from "containers/report/allPeriodReportTable/AssetRegisterReportTable";
import { startCase } from "lodash-es";
import type { BillingReportRequestParams } from "./BillingReportSelectorForm";

const dateToString = (
  date: Date | string,
  dateFormat?: string,
  timezone?: string,
): string => {
  const m = dayjs(date).tz(timezone ? timezone : "UTC");
  return dateFormat ? m.format(dateFormat) : m.format();
};

const mapBillingData = (
  data: AssetRegisterAggregatedTimeSeriesResponse,
  query: Required<BillingReportRequestParams>,
  dateFormat?: string,
  timezone?: string,
): Array<BillingData> => {
  return data.data.map((assetData) => {
    const { aggregations, ...asset } = assetData;

    switch (asset.asset_type) {
      case "heat_meter":
      case "heating_and_cooling_meter":
        return {
          ...asset,
          billable_type: "Energy (Heating)",
          billable_unit: query.configuration.energy?.units || "kWh",
          data: Object.fromEntries(
            data.times.map((e, i) => {
              return [
                dayjs(e).format(),
                aggregations["Energy (Heating)"]?.last?.[i],
              ];
            }),
          ),
        };
      case "cooling_meter":
        return {
          ...asset,
          billable_type: "Energy (Cooling)",
          billable_unit: query.configuration.energy?.units || "kWh",
          data: Object.fromEntries(
            data.times.map((e, i) => {
              return [
                dayjs(e).format(),
                aggregations["Energy (Cooling)"]?.last?.[i],
              ];
            }),
          ),
        };
      case "electricity_meter":
        return {
          ...asset,
          billable_type: "Energy (Electrical Active Import)",
          billable_unit: query.configuration.energy?.units || "kWh",
          data: Object.fromEntries(
            data.times.map((e, i) => {
              return [
                dayjs(e).format(),
                aggregations["Energy (Electrical Active Import)"].last?.[i],
              ];
            }),
          ),
        };
      default:
        return {
          ...asset,
          billable_type: "volume",
          billable_unit: query.configuration.volume?.units || "m3",
          data: Object.fromEntries(
            data.times.map((e, i) => {
              return [dayjs(e).format(), aggregations.Volume.last?.[i]];
            }),
          ),
        };
    }
  });
};

const BillingReportView = (props: {
  scheme_id: string;
  data: AssetRegisterAggregatedTimeSeriesResponse;
  query: Required<BillingReportRequestParams>;
}): React.ReactElement => {
  const billingData = mapBillingData(props.data, props.query);

  const periodLabel = props.query.configuration.period_label;
  const period = props.query.period;

  const mappedPeriod = {
    DAILY: "day",
    WEEKLY: "week",
    MONTHLY: "month",
    SEVEN_DAY: "seven day period",
    ALL: "all",
  }[period];

  const columns: PrivacyModeTableColumn<BillingData>[] = [
    {
      name: "Location",
      selector: locationSortValue,
      cell: LocationRow(props.query.start_time, props.query.end_time),
      sortable: true,
      width: "250px",
      compact: true,
      privacyMode: true,
    },
    {
      name: "Asset Position",
      selector: assetPositionSortValue,
      cell: AssetPositionRow(props.query.start_time, props.query.end_time),
      sortable: true,
      width: "200px",
      compact: true,
      privacyMode: true,
    },
    {
      name: "Asset Type",
      selector: (row: BillingData) => row.asset_type,
      cell: (row: BillingData) => startCase(row.asset_type),
      sortable: true,
      compact: true,
      width: "120px",
    },
    {
      name: "Asset",
      selector: (row: BillingData) => row.serial_number || "-",
      sortable: true,
      cell: AssetRow(props.query.start_time, props.query.end_time, false, true),
      width: "150px",
      compact: true,
      privacyMode: true,
    },
    {
      name: "Type",
      selector: (row: BillingData) => row.billable_type,
      width: "150px",
      compact: true,
    },
    {
      name: "Units",
      selector: (row: BillingData) => row.billable_unit,
      width: "75px",
      compact: true,
    },
    ...props.data.times.map((value) => {
      return {
        name: dayjs(value).format("DD/MM/YYYY"),
        selector: (row: BillingData) =>
          row.data[dayjs(value).format()] as string | number,
        right: true,
        sortable: true,
        width: "100px",
        compact: true,
        format: (row: BillingData) =>
          numberFormatter(dayjs(value).format())(row.data),
      };
    }),
  ];

  return (
    <>
      <div className={"w-full text-right"}>
        <CSVExportButton
          download={aggregatedDataCSVExport(billingData)}
          camelCaseColumns={false}
          columnOrder={[
            ...DEFAULT_ASSET_REGISTER_DATA_EXPORT_COLUMN_ORDER,
            "billableType",
            "billableUnit",
            ...props.data.times.map((value) =>
              dayjs(value).format("DD/MM/YYYY"),
            ),
          ]}
          filename={`${props.scheme_id}_billing_report_${dateToString(
            props.data.times[0],
            "YYYY-MM-DD",
            "Europe/London",
          )}_${dateToString(
            props.data.times[props.data.times.length - 1],
            "YYYY-MM-DD",
            "Europe/London",
          )}`}
        />
      </div>
      {period !== "DAILY" &&
        (periodLabel === "left"
          ? `Last reading for ${mappedPeriod} beginning on`
          : `Last reading for ${mappedPeriod} ending on`)}
      <PrivacyModeDataTable
        columns={columns}
        data={billingData}
        pagination
        fixedHeader
        noHeader={true}
      />
    </>
  );
};

export default BillingReportView;
